import { ReportJSON, isDefined } from '@aloft/toolkit-shared';

import {
  COST_APPROACH_SITE_COMMENT,
  getCostApproachReplacementCostComment,
} from '../../../../summaryV2/CostApproachSummary/consts.ts';
import { scalarToNumberToString } from '../../../utils.ts';
import { ClickFormsExportRow } from '../../types.ts';
import { fillInBlankRows } from '../../utils.ts';
import { generate1004LikeAdjustmentRows } from '../fnma1004/adjustments.ts';
import {
  FNMA1004C_ADJUSTMENT_FIELDS,
  FNMA1004C_ROWS,
  FNMA1004C_SALES_COMP_GRID_ROWS_PER_COMP,
  FNMA1004C_TOTAL_ROWS,
} from './consts.ts';

export const generateFnma1004CFormString = (
  compSequence: string[],
  reportJSON: ReportJSON,
  isMerge: boolean,
): string => {
  let rows: ClickFormsExportRow[] = [];
  const HEADER_STRING = isMerge ? 'CMD.MERGE.F342.I1.BROADCAST\n' : 'CMD.LOAD.F342\n'; // F340 is code for FNMA1004
  let result = HEADER_STRING;

  const subjectRows = generateSubjectRows(reportJSON.meta);
  rows = rows.concat(subjectRows);

  const { comps } = reportJSON;

  compSequence.forEach((compID, index) => {
    const comp = comps[compID];

    if (comp) {
      rows = rows.concat(
        generate1004LikeAdjustmentRows({
          comp: comp.salesGridData,
          compIndex: index,
          fieldMapping: FNMA1004C_ADJUSTMENT_FIELDS,
          rowsPerColumn: FNMA1004C_SALES_COMP_GRID_ROWS_PER_COMP,
        }),
      );
    }
  });

  // Cost Approach
  rows = rows.concat(generateCostApproachRows(reportJSON));

  result += fillInBlankRows(rows, FNMA1004C_TOTAL_ROWS);
  return result;
};

const generateSubjectRows = (meta: ReportJSON['meta']): ClickFormsExportRow[] => {
  const rows: ClickFormsExportRow[] = [];

  rows.push({ row: FNMA1004C_ROWS.SU_BLANK1_LABEL, value: 'Add. Features' });
  if (isDefined(meta?.otherFeature2Label)) {
    rows.push({ row: FNMA1004C_ROWS.SU_BLANK2_LABEL, value: meta?.otherFeature2Label });
  }
  return rows;
};

const generateCostApproachRows = ({ subject: { costApproach } }: ReportJSON) => {
  const { remainingEcoLife, effectiveAge } = costApproach ?? {};
  const rows: ClickFormsExportRow[] = [];

  rows.push({
    row: FNMA1004C_ROWS.CA_SUPPORT_FOR_SITE_VALUE,
    value: COST_APPROACH_SITE_COMMENT,
  });
  rows.push({ row: FNMA1004C_ROWS.CA_REPLACEMENT_COST_NEW_CHECKBOX, value: 'X' });

  rows.push({
    row: FNMA1004C_ROWS.CA_COMMENTS,
    value: getCostApproachReplacementCostComment(costApproach.source?.name ?? 'Craftsman'),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_COST_DATA_SOURCE,
    value: costApproach?.source?.name,
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_QUALITY,
    value: costApproach?.dwellingCostQuality,
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_EFFECTIVE_DATE,
    value: costApproach?.source?.effectiveDate.toString(),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_ESTIMATED_REMAINING_ECON_LIFE,
    value: `${remainingEcoLife ?? ''}`,
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_EFFECTIVE_AGE,
    value: `${effectiveAge ?? ''}`,
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_OPINION_OF_SITE_VALUE,
    value: scalarToNumberToString(costApproach?.site),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_SECTION1_SQFT,
    value: scalarToNumberToString(costApproach?.dwelling?.sqft),
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_SECTION1_COST_PER_SQFT,
    value: scalarToNumberToString(costApproach?.dwelling?.costPerSqft),
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_SECTION1_COST,
    value: scalarToNumberToString(costApproach?.dwelling?.totalCost),
  });

  const numSections = costApproach.sections ?? 1;

  if (numSections > 1) {
    rows.push({
      row: FNMA1004C_ROWS.CA_SECTION2_COST_PER_SQFT,
      value: scalarToNumberToString(costApproach?.dwelling?.costPerSqft),
    });
  }
  if (numSections > 2) {
    rows.push({
      row: FNMA1004C_ROWS.CA_SECTION3_COST_PER_SQFT,
      value: scalarToNumberToString(costApproach?.dwelling?.costPerSqft),
    });
  }
  if (numSections > 3) {
    rows.push({
      row: FNMA1004C_ROWS.CA_SECTION4_COST_PER_SQFT,
      value: scalarToNumberToString(costApproach?.dwelling?.costPerSqft),
    });
  }

  rows.push({
    row: FNMA1004C_ROWS.CA_OTHER1_LABEL,
    value: costApproach?.basement ? 'Basement' : '',
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_OTHER1_SQFT,
    value: scalarToNumberToString(costApproach?.basement?.sqft),
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_OTHER1_COST_PER_SQFT,
    value: scalarToNumberToString(costApproach?.basement?.costPerSqft),
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_OTHER1_COST,
    value: scalarToNumberToString(costApproach?.basement?.totalCost),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_OTHER2,
    value: costApproach?.additionalStructures ? 'Add. Structures' : '',
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_OTHER2_COST,
    value: scalarToNumberToString(costApproach?.additionalStructures?.totalCost),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_OTHER3,
    value: costApproach?.garageCarport ? 'Garage/Carport' : '',
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_OTHER3_COST,
    value: scalarToNumberToString(costApproach?.garageCarport?.totalCost),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_SUBTOTAL,
    value: scalarToNumberToString(costApproach?.new?.cost),
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_MODIFIED_SUBTOTAL,
    value: scalarToNumberToString(costApproach?.new?.cost),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_DEPR_PHYS,
    value: scalarToNumberToString(costApproach?.depreciation?.physical?.abs()),
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_DEPR_FUNC,
    value: scalarToNumberToString(costApproach?.depreciation?.functional?.abs()),
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_DEPR_EXTN,
    value: scalarToNumberToString(costApproach?.depreciation?.external?.abs()),
  });
  rows.push({
    row: FNMA1004C_ROWS.CA_MARKET_VALUE_OF_SITE,
    value: scalarToNumberToString(costApproach?.site),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_TOTAL_GLA,
    value: scalarToNumberToString(costApproach?.dwelling?.sqft),
  });

  rows.push({
    row: FNMA1004C_ROWS.CA_INDICATED_VALUE,
    value: scalarToNumberToString(costApproach?.indicatedValue),
  });

  return rows;
};
